"use client";

import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  paymentConfig,
  paymentEndpoints,
  getBaseUrl,
  getAuthHeader,
} from "../payment-config";

// Payment widget callback types
interface PaymentOptions {
  [key: string]: unknown;
}

interface PaymentResult {
  status?: string;
  transactionId?: string;
  [key: string]: unknown;
}

interface PaymentCallbacks {
  onSuccess?: (options?: PaymentOptions) => void;
  onFail?: (reason?: string | Error, options?: PaymentOptions) => void;
  onComplete?: (
    paymentResult?: PaymentResult,
    options?: PaymentOptions
  ) => void;
}

export class StripePaymentWidget {
  private stripe: Stripe | null = null;

  constructor() {
    this.initializeStripe();
  }

  private async initializeStripe() {
    if (!paymentConfig.stripe.publishableKey) {
      throw new Error("Stripe publishable key is not configured");
    }
    this.stripe = await loadStripe(paymentConfig.stripe.publishableKey, {
      locale: "en",
    });
  }

  public async initiatePayment(
    tokenCount: number,
    amount: number,
    currency: string,
    userId: string,
    userEmail: string,
    callbacks: PaymentCallbacks
  ) {
    try {
      if (!this.stripe) {
        await this.initializeStripe();
      }

      // Create Checkout Session through our backend
      const baseUrl = getBaseUrl();
      const apiUrl = `${baseUrl}${paymentEndpoints.stripe.createCheckoutSession}`;

      const authHeader = await getAuthHeader();

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100), // Stripe uses cents
          currency: currency.toLowerCase(),
          token_count: tokenCount,
          user_id: userId,
          user_email: userEmail,
          description: `Purchase ${tokenCount} tokens`,
          return_url: window.location.href, // Current page URL
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("HTTP error response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${errorText}`
        );
      }

      const responseData = await response.json();

      const { session_id } = responseData;

      if (!session_id) {
        throw new Error("No session_id received from server");
      }

      // Use Stripe Checkout for web applications

      if (!this.stripe) {
        throw new Error("Failed to initialize Stripe");
      }

      // Redirect to Stripe Checkout
      const result = await this.stripe.redirectToCheckout({
        sessionId: session_id,
      });

      if (result.error) {
        console.error("Stripe redirect error:", result.error);
        callbacks.onFail?.(new Error(result.error.message), {
          tokenCount,
          amount,
        });
      } else {
        // Redirect will happen automatically
      }
    } catch (error) {
      console.error("Stripe payment error:", error);
      const errorMessage =
        error instanceof Error ? error : new Error(String(error));
      callbacks.onFail?.(errorMessage, { tokenCount, amount });
    }
  }
}
